import { t } from '@/shared/i18n';
import { readStorage, writeStorage } from '@/shared/lib/storage';

/**
 * Как часто это устройство освежает офлайн-копию. Сообщения из очереди уходят сразу, как только есть связь, —
 * настройка про чтение: переписки, документы, календарь и задачи.
 */
export const SYNC_MODES = ['instant', 'm5', 'm15', 'h1', 'manual'] as const;
export type SyncMode = (typeof SYNC_MODES)[number];

/** Режим сменили в настройках: расписание подхватывает его на лету. */
export const SYNC_MODE_EVENT = 'chat-sync-mode';
const KEY = 'chat.offline.sync';
const LAST = 'chat.offline.lastSync';

export function readSyncMode(): SyncMode {
  const raw = readStorage(KEY);
  return SYNC_MODES.find((m) => m === raw) ?? 'instant';
}

export function writeSyncMode(mode: SyncMode): void {
  writeStorage(KEY, mode);
  window.dispatchEvent(new Event(SYNC_MODE_EVENT));
}

/** Период в миллисекундах; null — без расписания (сразу по событиям или только вручную). */
export function syncPeriodMs(mode: SyncMode): number | null {
  if (mode === 'm5') return 5 * 60_000;
  if (mode === 'm15') return 15 * 60_000;
  if (mode === 'h1') return 60 * 60_000;
  return null;
}

export function syncModeLabel(mode: SyncMode): string {
  if (mode === 'instant') return t('Сразу, как только есть связь');
  if (mode === 'm5') return t('Раз в 5 минут');
  if (mode === 'm15') return t('Раз в 15 минут');
  if (mode === 'h1') return t('Раз в час');
  return t('Только когда я нажму');
}

export function readLastSync(): number | null {
  const n = Number(readStorage(LAST));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function writeLastSync(at: number): void {
  writeStorage(LAST, String(at));
}

/** Пора ли освежать копию при возвращении связи. */
export function dueOnReconnect(mode: SyncMode, last: number | null, now: number): boolean {
  if (mode === 'instant') return true;
  const period = syncPeriodMs(mode);
  return period !== null && (last === null || now - last >= period);
}
